import { ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Card from '@rio-cloud/rio-uikit/Card';
import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import RadioButton from '@rio-cloud/rio-uikit/RadioButton';
import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';

import {
    CUSTOMER_FEEDBACK_OPTIONS,
    type CustomerFeedbackClassification,
    type FeedbackFormValues,
    OTHERS_CLASSIFICATION,
    WORKSHOP_FEEDBACK_OPTIONS,
    type WorkshopFeedbackClassification,
} from './utils/types';

export interface FeedbackFormProps {
    values: FeedbackFormValues;
    onChange: (values: FeedbackFormValues) => void;
    onSave: () => void | Promise<void>;
    onCancel: () => void;
    onDelete: () => void;
    canDelete: boolean;
    isSaving: boolean;
}

const isValid = (values: FeedbackFormValues): boolean => {
    if (values.serviceActionRequired === undefined) {
        return false;
    }
    if (values.workshopFeedback === OTHERS_CLASSIFICATION && values.workshopFeedbackOthers.trim() === '') {
        return false;
    }
    if (values.customerFeedback === OTHERS_CLASSIFICATION && values.customerFeedbackOthers.trim() === '') {
        return false;
    }
    return true;
};

const FeedbackForm = ({ values, onChange, onSave, onCancel, onDelete, canDelete, isSaving }: FeedbackFormProps) => {
    const intl = useIntl();

    const workshopFeedbackOptions: SelectOption[] = WORKSHOP_FEEDBACK_OPTIONS.map((classification) => ({
        id: classification,
        label: intl.formatMessage({ id: `intl-msg:vehicle-warnings.feedback.classification.${classification}` }),
        selected: values.workshopFeedback === classification,
    }));

    const customerFeedbackOptions: SelectOption[] = CUSTOMER_FEEDBACK_OPTIONS.map((classification) => ({
        id: classification,
        label: intl.formatMessage({ id: `intl-msg:vehicle-warnings.feedback.classification.${classification}` }),
        selected: values.customerFeedback === classification,
    }));

    const handleServiceActionRequiredChange = (serviceActionRequired: boolean) => {
        onChange({ ...values, serviceActionRequired });
    };

    const handleWorkshopFeedbackChange = (option: SelectOption | undefined) => {
        onChange({
            ...values,
            workshopFeedback: option?.id as WorkshopFeedbackClassification | undefined,
            workshopFeedbackOthers: option?.id === OTHERS_CLASSIFICATION ? values.workshopFeedbackOthers : '',
        });
    };

    const handleCustomerFeedbackChange = (option: SelectOption | undefined) => {
        onChange({
            ...values,
            customerFeedback: option?.id as CustomerFeedbackClassification | undefined,
            customerFeedbackOthers: option?.id === OTHERS_CLASSIFICATION ? values.customerFeedbackOthers : '',
        });
    };

    const handleWorkshopFeedbackOthersChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        onChange({ ...values, workshopFeedbackOthers: event.target.value });
    };

    const handleCustomerFeedbackOthersChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        onChange({ ...values, customerFeedbackOthers: event.target.value });
    };

    const handleCommentsChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        onChange({ ...values, comments: event.target.value });
    };

    return (
        <div className="display-flex flex-column gap-20">
            <div className="form-group">
                <FormLabel>
                    <FormattedMessage id="intl-msg:vehicle-warnings.feedback.serviceActionRequired" />
                </FormLabel>
                <div className="display-flex gap-20">
                    <RadioButton
                        name="serviceActionRequired"
                        inline
                        checked={values.serviceActionRequired === true}
                        onChange={() => handleServiceActionRequiredChange(true)}
                    >
                        <FormattedMessage id="intl-msg:vehicle-warnings.feedback.yes" />
                    </RadioButton>
                    <RadioButton
                        name="serviceActionRequired"
                        inline
                        checked={values.serviceActionRequired === false}
                        onChange={() => handleServiceActionRequiredChange(false)}
                    >
                        <FormattedMessage id="intl-msg:vehicle-warnings.feedback.no" />
                    </RadioButton>
                </div>
            </div>

            <div className="display-grid grid-cols-1 grid-cols-2-md gap-20">
                <Card>
                    <div className="display-flex flex-column gap-10">
                        <span className="text-italic text-medium">
                            <FormattedMessage id="intl-msg:vehicle-warnings.feedback.workshopFeedback.title" />
                        </span>
                        <div className="form-group">
                            <FormLabel htmlFor="workshopFeedback">
                                <FormattedMessage id="intl-msg:vehicle-warnings.feedback.classificationLabel" />
                            </FormLabel>
                            <Select
                                id="workshopFeedback"
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:vehicle-warnings.feedback.classificationPlaceholder',
                                })}
                                options={workshopFeedbackOptions}
                                onChange={handleWorkshopFeedbackChange}
                                useClear
                            />
                        </div>
                        {values.workshopFeedback === OTHERS_CLASSIFICATION && (
                            <div className="form-group">
                                <FormLabel htmlFor="workshopFeedbackOthers">
                                    <FormattedMessage id="intl-msg:vehicle-warnings.feedback.detailsLabel" />
                                </FormLabel>
                                <textarea
                                    id="workshopFeedbackOthers"
                                    className="form-control"
                                    rows={3}
                                    value={values.workshopFeedbackOthers}
                                    onChange={handleWorkshopFeedbackOthersChange}
                                />
                            </div>
                        )}
                    </div>
                </Card>

                <Card>
                    <div className="display-flex flex-column gap-10">
                        <span className="text-italic text-medium">
                            <FormattedMessage id="intl-msg:vehicle-warnings.feedback.customerFeedback.title" />
                        </span>
                        <div className="form-group">
                            <FormLabel htmlFor="customerFeedback">
                                <FormattedMessage id="intl-msg:vehicle-warnings.feedback.classificationLabel" />
                            </FormLabel>
                            <Select
                                id="customerFeedback"
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:vehicle-warnings.feedback.classificationPlaceholder',
                                })}
                                options={customerFeedbackOptions}
                                onChange={handleCustomerFeedbackChange}
                                useClear
                            />
                        </div>
                        {values.customerFeedback === OTHERS_CLASSIFICATION && (
                            <div className="form-group">
                                <FormLabel htmlFor="customerFeedbackOthers">
                                    <FormattedMessage id="intl-msg:vehicle-warnings.feedback.detailsLabel" />
                                </FormLabel>
                                <textarea
                                    id="customerFeedbackOthers"
                                    className="form-control"
                                    rows={3}
                                    value={values.customerFeedbackOthers}
                                    onChange={handleCustomerFeedbackOthersChange}
                                />
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            <div className="form-group">
                <FormLabel htmlFor="comments">
                    <FormattedMessage id="intl-msg:vehicle-warnings.feedback.commentsLabel" />
                </FormLabel>
                <textarea
                    id="comments"
                    className="form-control"
                    rows={4}
                    value={values.comments}
                    onChange={handleCommentsChange}
                />
            </div>

            <ButtonToolbar alignment="space-between">
                {canDelete ? (
                    <Button bsStyle="danger" variant="outline" onClick={onDelete} iconName="trash">
                        <FormattedMessage id="intl-msg:vehicle-warnings.feedback.delete" />
                    </Button>
                ) : (
                    <span />
                )}
                <ButtonToolbar>
                    <Button onClick={onCancel}>
                        <FormattedMessage id="intl-msg:vehicle-warnings.feedback.discardChanges" />
                    </Button>
                    <Button bsStyle="primary" onClick={onSave} disabled={!isValid(values) || isSaving}>
                        <FormattedMessage id="intl-msg:vehicle-warnings.feedback.save" />
                    </Button>
                </ButtonToolbar>
            </ButtonToolbar>
        </div>
    );
};

export default FeedbackForm;
